/**
 * A loadClientFake a kivezetett swagger/openapi kliens helyett van
 */

import axios from './axiosClient'
import operations from './operations'
import { memoize } from './utils/memoize'
import AuthProvider from './lib/auth/provider'
import { isUndefined, isNull, isEmpty, isObject, cloneDeep, get } from 'lodash'
import { CSRF_HEADER } from './lib/cookies/cookies'

/**
 *
 * @param value
 * @return {boolean}
 */
const isSendableValue = (value) => !(isUndefined(value) || isNull(value) || value === '')

/**
 * @description api hivas
 * @type {Readonly<{process: ((function(): Promise<*>)|*), clear: clear}>}
 */
const execApiCall = memoize(async function (operationId, parameters, client) {
  try {
    return await client.execute({
      operationId,
      parameters,
    })
  } catch (e) {
    console.error('error in request: %o', e.message)
    return ''
  }
})

/**
 * @description url felepitese
 * @param url
 * @param setup
 * @param method
 * @return {string|*}
 */
function buildUrl(url, setup, method) {
  let qs = ''

  let parameters = {}

  if (method === 'post' || method === 'put') {
    if (isEmpty(setup.operation.queryKeys)) {
      return url
    }
    Object.keys(setup.parameters).forEach((key) => {
      if (setup.operation.queryKeys.includes(key)) {
        parameters[key] = setup.parameters[key]
      }
    })
  } else {
    parameters = setup.parameters
  }

  for (const key in parameters) {
    if (parameters.hasOwnProperty(key)) {
      const value = parameters[key]
      if (isSendableValue(value)) {
        qs += encodeURIComponent(key) + '=' + encodeURIComponent(value) + '&'
      }
    }
  }
  if (qs.length > 0) {
    qs = qs.substring(0, qs.length - 1)
    url = url + '?' + qs
  }
  return url
}

/**
 * @description post keres kuldese
 * @param url
 * @param setup
 * @param method
 * @return {Promise<Response>}
 */
async function buildPostRequest(url, setup, method) {
  const parameters = setup.operation.dataKey ? setup.parameters[setup.operation.dataKey] : setup.parameters

  let body
  let headers = cloneDeep(setup.headers)

  if (get(window.CORE, 'env.csrfEnabled')) {
    try {
      const response = await axios().get('/csrf-token')
      headers[CSRF_HEADER] = response.data.csrfToken
    } catch (error) {
      console.error('Failed to retrieve CSRF token:', error)
      throw new Error('CSRF token retrieval failed')
    }
  }

  if (setup.operation.contentType && setup.operation.contentType === 'application/json') {
    body = {}
    Object.keys(parameters).forEach((key) => {
      let value = parameters[key]
      if (isSendableValue(value)) {
        body[key] = value
      }
    })
    body = JSON.stringify(body)
    headers['Content-Type'] = setup.operation.contentType
  } else {
    body = new FormData()
    Object.keys(parameters).forEach((key) => {
      let value = parameters[key]
      if (isSendableValue(value)) {
        if (isObject(value)) {
          value = JSON.stringify(value)
        }
        body.append(key, value)
      }
    })
  }

  return fetch(url, {
    method,
    body,
    headers,
  })
}

/**
 * @description get keres kuldese
 * @param url
 * @param setup
 * @returns {Promise<Response>|*}
 */
function buildGetRequest(url, setup) {
  try {
    return fetch(url, { headers: setup.headers, parameters: setup.parameters })
  } catch (e) {
    return e
  }
}

function hasLocation(header) {
  return ['x-location', 'location'].includes(header[0])
}

/**
 * @description http kliens osztaly
 */
class SwaggerClient {
  /**
   * @description peldany inicializalasa
   * @param setup
   */
  constructor(setup = {}) {
    this.fake = true
    this.spec = {
      basePath: '/api',
    }

    this.apis = {}
    Object.keys(operations).forEach((operationId) => {
      const [modelName, methodName] = operationId.split('_')
      if (typeof undefined === typeof this.apis[modelName]) {
        this.apis[modelName] = {}
      }
      this.apis[modelName][operationId] = (parameters) => this.execute({ operationId, parameters })
    })

    this.headers = setup.headers || {}
    this.memo = memoize
  }
  /**
   * @description Kérés küldése a szerver felé streamelve
   * @param {string} endpoint - A végpont URL-je
   * @param {object} params - Az URL query paraméterei
   * @param {function} onMessage - A callback függvény, amely az új bejegyzés beérkezésekor hajtódik végre
   * @param {function} onEnd - A callback függvény, amely az utolsó bejegyzés után hajtódik végre
   * @param {function} onError - A callback függvény, amely hiba esetén hajtódik végre
   * @param {function} emitOnMessage - Message tipusú eventek engedélyezésének kapcsolója
   */
  streamEvent(endpoint, params, onMessage = () => {}, onEnd = () => {}, onError = () => {}, emitOnMessage = false) {
    const queryParams = new URLSearchParams(params)
    const urlWithParams = `${endpoint}?${queryParams.toString()}`
    const eventSource = new EventSource(urlWithParams)

    eventSource.addEventListener('data', (event) => {
      if (!event) {
        eventSource.close()
      } else {
        const data = JSON.parse(event.data)
        if (event.data.includes('stream-end')) {
          eventSource.close()
          onEnd(data)
        } else {
          onMessage(data)
        }
      }
    })

    eventSource.onmessage = (event) => {
      if (!event) {
        eventSource.close()
      } else {
        const data = JSON.parse(event.data)
        if (event.data.includes('stream-end')) {
          eventSource.close()
          onEnd(data)
        } else {
          if (emitOnMessage) onMessage(data)
        }
      }
    }

    eventSource.onerror = function (error) {
      eventSource.close()
      try {
        error = JSON.parse(error.data)
        if (error) onError(error.message)
      } catch (e) {
        onError(e)
      }
    }
  }
  /**
   * @description keres kuldese szerver fele
   * @param setup
   * @returns {Promise<{data: *, obj: *, body: *, status}|*>}
   */
  async execute(setup) {
    this.validateSetup(setup)
    setup.operation = operations[setup.operationId]
    this.headers['Cache-Control'] = 'no-cache, no-store, max-age=0'
    this.headers['X-Requested-With'] = 'XMLHttpRequest'
    this.headers['Routed-Url'] = get(window, 'CORE.routedUrl', window.location.href)

    const authenticator = AuthProvider.getAuthenticator()

    if (authenticator.getToken()) {
      this.headers['Authorization'] = `Bearer ${authenticator.getToken()}`
    }

    const method = setup.operation.method || setup.method || 'get'
    const url = buildUrl(`${this.spec.basePath}${setup.operation.path}`, setup, method)
    setup.headers = Object.assign({}, this.headers, setup.headers)

    let request = null
    try {
      switch (method) {
        case 'get':
          request = buildGetRequest(url, setup)
          break
        case 'post':
        case 'put':
          request = buildPostRequest(url, setup, method)
          break
      }
    } catch (e) {
      return e
    }

    const response = await request

    for (const header of response.headers.entries()) {
      if (hasLocation(header)) {
        return (window.location.href = header[1])
      }
    }

    let result
    let resultText
    try {
      result = await response.clone().json()
      resultText = await response.clone().text()
    } catch (e) {
      result = await response.clone().blob()
    }

    if (setup.operation.first) {
      result = result[0]
    }

    return {
      status: response.status,
      body: result,
      obj: result,
      data: resultText,
    }
  }

  /**
   * @description cachelt kuldes
   * @param operationId
   * @param parameters
   * @returns {Promise<*>}
   */
  async executeCached(operationId, parameters) {
    return await execApiCall.process(operationId, parameters, this)
  }

  /**
   * @description kezdeti parameterek ellenorzese
   * @param setup
   */
  validateSetup(setup) {
    if (!setup.operationId) {
      console.log(setup)
      throw new Error('operationId missing from setup')
    }
    if (!operations[setup.operationId]) {
      console.log(setup)
      throw new Error('operationId missing from operations')
    }
  }
}

export default function loadClient(setup) {
  return new SwaggerClient(setup)
}
